import { Button } from "@/components/ui/button";
import { GetPayslipUserResult } from "@/models/payslips.model";
import { months, toDateFormatted, toDateMonthYear } from "@/utils/date-format";
import { ColumnDef } from "@tanstack/react-table";
import { Plus, ArrowUpDown } from "lucide-react";
import ProcessingStatusTag from "../../../components/tags/ProcessingStatusTag";
import { useNavigate } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import EmploymentStatusTag from "@/components/tags/EmploymentStatusTag";

const ActionCell = ({
  row,
  selectedMonth,
  selectedYear,
}: {
  row: any;
  selectedMonth: string;
  selectedYear: string;
}) => {
  const navigate = useNavigate();
  return (
    <Button
      variant="ghost"
      className="h-8 w-8 p-0"
      onClick={() =>
        navigate(
          `/admin/users/${row.original.user.id}/pay-items/${selectedYear}/${months.indexOf(selectedMonth) + 1
          }`,
          {
            state: {
              processingStatus: row.original.processingStatus,
              user: row.original.user,
            },
          }
        )
      }
    >
      <Plus className="h-4 w-4" />
    </Button>
  );
};

export const getColumns = (
  selectedMonth: string,
  selectedYear: string
): ColumnDef<GetPayslipUserResult>[] => [
    {
      id: "select",
      header: ({ table }) => (
        <div className="sticky left-0 z-30 px-4">
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && "indeterminate")
            }
            onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
          />
        </div>
      ),
      cell: ({ row }) => (
        <div className="left-0 px-4">
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        </div>
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "employeeName",
      accessorFn: (row) => `${row.user.fullName}`,
      header: ({ column }) => {
        return (
          <div className="flex items-center gap-2">
            Emloyee
            <button
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            >
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    {
      id: "hireDate",
      accessorFn: (row) => row.user.employmentInfo.hireDate,
      cell: ({ row }) => {
        return (
          <span>
            {toDateMonthYear(row.original.user.employmentInfo.hireDate)}{" "}
          </span>
        );
      },
      header: ({ column }) => {
        return (
          <div className="flex items-center gap-2">
            Hire Date
            <button
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            >
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </button>
          </div>
        );
      },
    },
    {
      id: "Employment Status",
      header: "Employment Status",
      cell: ({ row }) => {
        return (
          <EmploymentStatusTag
            status={row.original.user.employmentInfo.employmentStatus}
          />
        );
      },
    },
    {
      accessorKey: "Processing Status",
      accessorFn: (row) => row.processingStatus,
      header: ({ column }) => {
        return (
          <div className="flex items-center gap-2">
            Processing Status
            <button
              onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            >
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </button>
          </div>
        );
      },
      cell: ({ row }) => {
        return <ProcessingStatusTag status={row.original.processingStatus} />;
      },
    },
    {
      id: "actions",
      cell: ({ row }) => (
        <ActionCell
          row={row}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
        />
      ),
    },
  ];
